import type { Message } from '@/components/layout/chat-pane.types'
import { getToolLabel } from '@/components/layout/chat-pane.utils'
import { isInternalWsStoragePath } from '@/lib/chat-feedback'
import {
  isHardToolFailure,
  isSoftToolFailure,
  softFailureDisplayText,
  toolResultFailureText,
} from '@/components/features/chat-messages/tool-message.soft-failure'
import {
  compactToolFootnote,
  isToolRunTerminal,
} from '@/components/features/chat-messages/tool-message'

export type ThinkingStep = {
  id: string
  label: string
  footnote?: string | null
  isRunning: boolean
  isSoftFailure: boolean
  isHardFailure: boolean
  failureText?: string | null
  toolName?: string
  toolResult?: unknown
  showDetails: boolean
}

export type ThinkingDocRow = {
  id: string
  title: string
  path: string
}

const WRITE_TOOLS = new Set([
  'createDocument',
  'uploadImage',
  'updateDocument',
  'appendToDocument',
  'patchDocument',
  'moveSection',
])
const WEB_TOOLS = new Set(['webSearch', 'fetchWebPage'])
const DOC_TOOLS = new Set([
  'searchDocuments',
  'semanticSearch',
  'getDocument',
  'readToolResult',
  'getTodoContext',
  'listFolderContents',
])
const BASECAMP_TOOLS = new Set([
  'fetchBasecampTodo',
  'getBasecampTodoComments',
  'getBasecampAssignments',
  'queryBasecampTodos',
  'searchBasecamp',
  'updateBasecampTodoNotes',
])

/** First-class widgets stay outside the Thinking block (design doc). */
export function isFirstClassToolWidget(message: Message): boolean {
  const toolName = message.toolName
  if (!toolName) return false

  if (toolName === 'createDocument' && message.toolResult?.success === true) {
    return true
  }

  if (
    (toolName === 'updateDocument' ||
      toolName === 'appendToDocument' ||
      toolName === 'patchDocument' ||
      toolName === 'moveSection') &&
    message.toolResult?.type === 'proposed_edit'
  ) {
    return true
  }

  if (toolName === 'createQuizDocument' && message.toolResult?.success === true) {
    return true
  }

  if (
    toolName === 'imageSearch' &&
    message.toolResult &&
    typeof message.toolResult === 'object' &&
    Array.isArray((message.toolResult as { results?: unknown }).results) &&
    ((message.toolResult as { results: unknown[] }).results.length ?? 0) > 0
  ) {
    return true
  }

  return false
}

/** Extract the search query from a searchDocuments tool result. */
function searchQueryFromMessage(message: Message): string | undefined {
  const tr = message.toolResult
  if (!tr || typeof tr !== 'object') return undefined
  const row = tr as Record<string, unknown>
  if (typeof row.query === 'string' && row.query.trim()) return row.query.trim()
  const fa = row.filtersApplied as Record<string, unknown> | undefined
  if (typeof fa?.query === 'string' && fa.query.trim()) return fa.query.trim()
  return undefined
}

/** Map a tool message to a quiet expanded-step label. */
function stepLabelForTool(message: Message): string {
  const toolName = message.toolName
  if (!toolName) return 'Working'

  if (toolName === 'readToolResult') return 'Spill read'
  if (toolName === 'searchDocuments' || toolName === 'semanticSearch') return 'Search documents'
  if (toolName === 'webSearch') return 'Web search'
  if (toolName === 'fetchWebPage') return 'Open page'
  if (toolName === 'getDocument') return 'Context read'

  return message.toolDisplayLabel || getToolLabel(toolName)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function argString(record: Record<string, unknown> | null, key: string): string {
  if (!record) return ''
  const value = record[key]
  return typeof value === 'string' ? value.trim() : ''
}

function clipStatus(text: string, max = 72): string {
  const compact = text.replace(/\s+/g, ' ').trim()
  if (compact.length <= max) return compact
  return `${compact.slice(0, max - 3)}...`
}

function friendlyArgPath(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return ''
  const base = value.split('/').pop() || value
  const name = base.replace(/\.(md|html)$/i, '').trim()
  return name.replace(/[-_]+/g, ' ')
}

function docTitle(value: unknown): string {
  const doc = asRecord(value)
  return argString(doc, 'title')
}

function titleFromDocResult(toolResult: Record<string, unknown> | null): string {
  if (!toolResult) return ''
  const direct = docTitle(toolResult.document) || argString(toolResult, 'title')
  if (direct) return direct
  const inner = asRecord(toolResult.result)
  if (!inner) return ''
  return docTitle(inner.document) || argString(inner, 'title')
}

function hostOf(url: string): string {
  if (!url) return ''
  try {
    return new URL(url).host
  } catch {
    return ''
  }
}

/**
 * One line for the collapsed status bar: the verb plus the subject
 * (document title, search query, page).
 */
export function liveStatusLineForTool(message: Message): string {
  const toolName = message.toolName
  const args = asRecord(message.toolArgs)
  const toolResult = asRecord(message.toolResult)

  if (!toolName) {
    return message.toolDisplayLabel?.trim() || 'Working'
  }

  if (
    toolName === 'getDocument' ||
    toolName === 'readToolResult' ||
    toolName === 'previewDocument'
  ) {
    const title =
      titleFromDocResult(toolResult) ||
      argString(args, 'title') ||
      friendlyArgPath(args?.filePath) ||
      friendlyArgPath(args?.path)
    return title ? `Reading ${clipStatus(title)}` : 'Reading a document'
  }

  if (toolName === 'searchDocuments' || toolName === 'semanticSearch') {
    const query = searchQueryFromMessage(message) || argString(args, 'query')
    return query ? `Searching "${clipStatus(query, 56)}"` : 'Searching documents'
  }

  if (toolName === 'webSearch') {
    const query = argString(args, 'query') || argString(toolResult, 'query')
    return query ? `Searching the web for "${clipStatus(query, 48)}"` : 'Searching the web'
  }

  if (toolName === 'fetchWebPage') {
    const url = argString(args, 'url') || argString(toolResult, 'url')
    const host = hostOf(url)
    return host ? `Opening ${host}` : 'Opening a page'
  }

  if (toolName === 'listFolderContents') {
    const folder = friendlyArgPath(args?.folderPath)
    return folder ? `Listing ${clipStatus(folder)}` : 'Listing a folder'
  }

  if (toolName === 'uploadImage') return 'Saving an image'

  if (WRITE_TOOLS.has(toolName) || toolName === 'createSkill') {
    const title =
      argString(args, 'title') ||
      argString(args, 'name') ||
      argString(args, 'slug') ||
      argString(toolResult, 'title')
    return title ? `Writing ${clipStatus(title)}` : 'Writing a file'
  }

  if (message.toolDisplayLabel?.trim()) return clipStatus(message.toolDisplayLabel.trim(), 80)
  return getToolLabel(toolName)
}

function dedupeConsecutive(lines: string[]): string[] {
  const out: string[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    if (out[out.length - 1] === trimmed) continue
    out.push(trimmed)
  }
  return out
}

/**
 * Ordered status-bar lines for one thinking group.
 * While the turn is live, the last line is the current step (or the in-flight
 * operation label, which arrives before the tool result). When the turn
 * settles, the summary is appended so the bar can scroll into it.
 */
export function buildLiveStatusScript(opts: {
  messages: Message[]
  isActive: boolean
  pendingLabel?: string | null
}): string[] {
  const stepLines = dedupeConsecutive(opts.messages.map(liveStatusLineForTool))
  if (!opts.isActive) {
    if (opts.messages.length === 0) return [THINKING_WAITING_HEADER]
    const summary = buildSettledHeader(opts.messages)
    return dedupeConsecutive([...stepLines, summary])
  }

  const pending = opts.pendingLabel?.trim() || ''
  const lines = pending ? dedupeConsecutive([...stepLines, pending]) : stepLines
  if (lines.length === 0) return [THINKING_WAITING_HEADER]
  return lines
}

/** Collect domain names (documents, web, Basecamp) used in a settled summary. */
function settledDomains(messages: Message[]): string[] {
  const domains: string[] = []
  let hasDocs = false
  let hasWeb = false
  let hasBasecamp = false

  for (const m of messages) {
    const n = m.toolName
    if (!n) continue
    if (DOC_TOOLS.has(n)) hasDocs = true
    if (WEB_TOOLS.has(n)) hasWeb = true
    if (BASECAMP_TOOLS.has(n) || n.toLowerCase().includes('basecamp')) hasBasecamp = true
  }

  if (hasDocs) domains.push('documents')
  if (hasWeb) domains.push('the web')
  if (hasBasecamp) domains.push('Basecamp')

  if (domains.length === 0) domains.push('tools')
  return domains
}

/** Build the one-line settled header after all tools in the turn finish. */
export function buildSettledHeader(messages: Message[]): string {
  const steps = buildThinkingSteps(messages)
  const domains = settledDomains(messages)
  const domainText =
    domains.length === 1
      ? domains[0] === 'documents'
        ? 'documents'
        : domains[0] === 'the web'
          ? 'the web'
          : domains[0]
      : domains.length === 2
        ? `${domains[0]} and ${domains[1]}`
        : `${domains.slice(0, -1).join(', ')}, and ${domains[domains.length - 1]}`

  return `Looked through ${domainText} · ${steps.length} step${steps.length === 1 ? '' : 's'}`
}

/** Live wait copy when the stream is open but no tool is in flight. */
export const THINKING_WAITING_HEADER = 'Thinking...'

/** Derive collapsed header copy for an in-flight or settled thinking group. */
export function deriveMorphHeader(
  messages: Message[],
  isActive: boolean,
  pendingLabel?: string | null,
): string {
  const script = buildLiveStatusScript({ messages, isActive, pendingLabel })
  return script[script.length - 1] ?? THINKING_WAITING_HEADER
}

/** Return a dedupe key for consecutive identical search tool calls. */
function searchDedupeKey(message: Message): string | null {
  const n = message.toolName
  if (n !== 'searchDocuments' && n !== 'semanticSearch') return null
  const q = searchQueryFromMessage(message) || ''
  return `${n}:${q.toLowerCase()}`
}

/** Build quiet expanded-step rows from a group of tool messages. */
export function buildThinkingSteps(messages: Message[]): ThinkingStep[] {
  const steps: ThinkingStep[] = []
  const seenSearch = new Map<string, number>()

  messages.forEach((message, index) => {
    const dedupeKey = searchDedupeKey(message)
    if (dedupeKey && seenSearch.has(dedupeKey)) {
      const existingIdx = seenSearch.get(dedupeKey)!
      const footnote = compactToolFootnote(message.toolName, message.toolResult)
      if (footnote) {
        steps[existingIdx] = {
          ...steps[existingIdx],
          footnote,
          isRunning: !isToolRunTerminal(message.toolResult),
          toolResult: message.toolResult,
        }
      }
      return
    }

    const failureText = toolResultFailureText(message.toolResult)
    const soft = isSoftToolFailure(message.toolResult)
    const hard = isHardToolFailure(message.toolResult)
    const running = !isToolRunTerminal(message.toolResult)
    const footnote =
      failureText && soft
        ? softFailureDisplayText(message.toolResult)
        : compactToolFootnote(message.toolName, message.toolResult)

    const step: ThinkingStep = {
      id: `step-${index}`,
      label: stepLabelForTool(message),
      footnote,
      isRunning: running,
      isSoftFailure: Boolean(failureText && soft),
      isHardFailure: hard,
      failureText: failureText && !soft ? failureText : null,
      toolName: message.toolName,
      toolResult: message.toolResult,
      showDetails: Boolean(message.toolResult && typeof message.toolResult === 'object'),
    }

    if (dedupeKey) seenSearch.set(dedupeKey, steps.length)
    steps.push(step)
  })

  return steps
}

/** Format a document path for Thinking UI, hiding internal ws/{hex} storage roots. */
export function formatThinkingDocPath(
  path: string | null | undefined,
  displayPath?: string | null,
  title?: string | null,
): string {
  const preferred = (displayPath || path || '').trim()
  if (!preferred) return title?.trim() || 'Document'
  if (isInternalWsStoragePath(preferred)) {
    if (title?.trim()) return title.trim()
    const base = preferred.split('/').pop()
    return base || 'Document'
  }
  return preferred.replace(/^\/+/, '')
}

/** Collect unique documents from search tool results for the expand doc list. */
export function extractThinkingDocuments(messages: Message[]): ThinkingDocRow[] {
  const seen = new Set<string>()
  const docs: ThinkingDocRow[] = []

  for (const message of messages) {
    if (message.toolName !== 'searchDocuments' && message.toolName !== 'semanticSearch') {
      continue
    }
    const tr = message.toolResult
    if (!tr || typeof tr !== 'object') continue
    const row = tr as Record<string, unknown>
    const list = (row.documents ?? row.results ?? row.hits) as
      | Array<Record<string, unknown>>
      | undefined
    if (!Array.isArray(list)) continue

    for (const doc of list) {
      const id = doc?.id != null ? String(doc.id) : ''
      if (!id || seen.has(id)) continue
      seen.add(id)
      const title =
        typeof doc.title === 'string' && doc.title.trim()
          ? doc.title.trim()
          : 'Untitled'
      const path = formatThinkingDocPath(
        typeof doc.path === 'string'
          ? doc.path
          : typeof doc.filePath === 'string'
            ? doc.filePath
            : undefined,
        typeof doc.displayPath === 'string' ? doc.displayPath : undefined,
        title,
      )
      docs.push({ id, title, path })
    }
  }

  return docs
}

/** Return the latest hard tool failure message for a single banner under the block. */
export function collectHardErrorBanner(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (isHardToolFailure(m.toolResult)) {
      return toolResultFailureText(m.toolResult) || 'Something went wrong'
    }
  }
  return null
}

/** True while tools are running or the last turn group is still streaming. */
export function thinkingTimelineIsActive(
  messages: Message[],
  isStreaming: boolean,
  isLastTurnGroup: boolean,
): boolean {
  if (messages.some((m) => !isToolRunTerminal(m.toolResult))) return true
  return isStreaming && isLastTurnGroup
}
